import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import InventoryLayout from './InventoryLayout';

const InventoryItems = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const loadItems = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/inventory/items', { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke hente varer');
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Kunne ikke hente varer');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) => {
      const name = (item.name || '').toLowerCase();
      const sku = (item.sku || '').toLowerCase();
      const barcode = (item.barcode || '').toLowerCase();
      const location = (item.location || '').toLowerCase();
      return (
        name.includes(query) ||
        sku.includes(query) ||
        barcode.includes(query) ||
        location.includes(query)
      );
    });
  }, [items, searchQuery]);

  const handleDisable = async (id) => {
    if (!window.confirm('Deaktiver denne vare?')) return;
    try {
      const res = await fetch(`/api/inventory/items/${id}`, { method: 'DELETE', headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke deaktivere vare');
      loadItems();
    } catch (err) {
      setError(err.message || 'Kunne ikke deaktivere vare');
    }
  };

  return (
    <InventoryLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Varer</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">Varer, lagerstatus og min. beholdning.</p>
        </div>
        <Link to="/lager/varer/ny" className="bg-falck-blue text-white px-4 py-2 rounded-md hover:bg-blue-700">
          Opret vare
        </Link>
      </div>

      {error && <div className="mb-3 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 dark:border-gray-800">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Søg varer</label>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
            placeholder="Søg på navn, varenummer, stregkode eller lokation..."
          />
        </div>
        {loading ? (
          <div className="p-4 text-gray-500">Indlæser...</div>
        ) : filteredItems.length === 0 ? (
          <div className="p-4 text-gray-500">Ingen varer fundet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left">Vare</th>
                  <th className="px-3 py-2 text-left">Varenummer</th>
                  <th className="px-3 py-2 text-left">Stregkode</th>
                  <th className="px-3 py-2 text-left">Lager</th>
                  <th className="px-3 py-2 text-left">Min</th>
                  <th className="px-3 py-2 text-left">Lokation</th>
                  <th className="px-3 py-2 text-left">Genbestil</th>
                  <th className="px-3 py-2 text-left">Handling</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredItems.map((item) => (
                  <tr key={item.id} className={!item.active ? 'opacity-60' : ''}>
                    <td className="px-3 py-2 font-medium">{item.name}</td>
                    <td className="px-3 py-2">{item.sku || '—'}</td>
                    <td className="px-3 py-2">{item.barcode || '—'}</td>
                    <td className="px-3 py-2">{item.stock_qty}</td>
                    <td className="px-3 py-2">{item.min_qty}</td>
                    <td className="px-3 py-2">{item.location || '—'}</td>
                    <td className="px-3 py-2">
                      {item.reorder_url ? (
                        <a
                          href={item.reorder_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-falck-blue hover:underline"
                        >
                          Link
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-2">
                        <Link to={`/lager/varer/${item.id}`} className="text-falck-blue hover:underline">
                          Rediger
                        </Link>
                        {item.active ? (
                          <button
                            type="button"
                            onClick={() => handleDisable(item.id)}
                            className="text-red-600 hover:underline"
                          >
                            Deaktiver
                          </button>
                        ) : (
                          <span className="text-gray-500">Deaktiveret</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </InventoryLayout>
  );
};

export default InventoryItems;
